import { useState } from "react";
import { motion } from "framer-motion";
import { Send, Mail, MessageCircle, MapPin, Instagram } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { CONTACT, whatsappLink } from "@/config/contact";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WorkWithMePage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/contact`, { ...form, type: "work-with-me" });
      toast.success(res.data.message);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    }
    setLoading(false);
  };

  return (
    <div className="pt-28 lg:pt-44" data-testid="work-with-me-page">
      <section className="py-10 md:py-14 bg-warmcream">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <p className="font-accent text-2xl text-sunset mb-1">collaboration</p>
              <h1 className="font-display text-4xl sm:text-5xl tracking-tight mb-4">
                Work With Me
              </h1>
              <p className="text-charcoal/60 leading-relaxed">
                Whether you're looking to book an appointment, have a question about products, are
                interested in collaborating, or just want to get in touch, I'd love to hear from you.
              </p>
            </div>
            <div className="flex justify-center">
              {/* Illustration kept for future use: https://static.prod-images.emergentagent.com/jobs/d354418a-0de3-4cd0-a1c7-b2b40d50fad0/images/71131d3d4b7ed1ac29a7860d3d5f517dd349b5632f9c5f4c572216faed695706.png */}
              <img
                src="https://customer-assets.emergentagent.com/job_0d39ab76-9d5c-4abc-8302-8d3f959ae156/artifacts/d44smv9t_DF27799B-691A-4046-83FE-EC669288FDB7-308-00000007FD222B13.jpg"
                alt="Jax on the Cornwall coast"
                className="max-w-sm w-full aspect-[3/4] object-cover rounded-3xl"
              />
            </div>
          </div>
        </div>
      </section>
      <section className="py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <h2 className="font-display text-2xl text-black mb-5">I'd love to hear from you about</h2>
              <ul className="space-y-2.5 mb-10">
                {[
                  "Hair appointments",
                  "Curl education",
                  "Brand collaborations",
                  "Content creation",
                  "Speaking opportunities",
                  "The Curl Edit",
                  "General enquiries",
                ].map((reason) => (
                  <li key={reason} className="flex items-center gap-3 text-charcoal/75 text-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-sage flex-shrink-0" />
                    {reason}
                  </li>
                ))}
              </ul>

              <div className="space-y-3" data-testid="contact-methods">
                <a
                  href={`mailto:${CONTACT.email}`}
                  className="flex items-center gap-4 bg-white rounded-2xl p-4 border border-ocean/5 hover:shadow-md transition-all"
                  data-testid="contact-email"
                >
                  <div className="w-11 h-11 rounded-xl bg-peachlight flex items-center justify-center flex-shrink-0">
                    <Mail className="text-sage" size={19} />
                  </div>
                  <div>
                    <p className="font-medium text-sm text-black">Email</p>
                    <p className="text-charcoal/60 text-sm">{CONTACT.email}</p>
                  </div>
                </a>

                {whatsappLink() ? (
                  <a
                    href={whatsappLink()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 bg-white rounded-2xl p-4 border border-ocean/5 hover:shadow-md transition-all"
                    data-testid="contact-whatsapp"
                  >
                    <div className="w-11 h-11 rounded-xl bg-peachlight flex items-center justify-center flex-shrink-0">
                      <MessageCircle className="text-sage" size={19} />
                    </div>
                    <div>
                      <p className="font-medium text-sm text-black">Business WhatsApp</p>
                      <p className="text-charcoal/60 text-sm">Send me a quick message</p>
                    </div>
                  </a>
                ) : (
                  <div className="flex items-center gap-4 bg-white rounded-2xl p-4 border border-ocean/5" data-testid="contact-whatsapp">
                    <div className="w-11 h-11 rounded-xl bg-peachlight flex items-center justify-center flex-shrink-0">
                      <MessageCircle className="text-sage" size={19} />
                    </div>
                    <div>
                      <p className="font-medium text-sm text-black">Business WhatsApp</p>
                      <p className="text-charcoal/60 text-sm">Coming soon</p>
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-4 bg-white rounded-2xl p-4 border border-ocean/5" data-testid="contact-address">
                  <div className="w-11 h-11 rounded-xl bg-peachlight flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-sage" size={19} />
                  </div>
                  <div>
                    <p className="font-medium text-sm text-black">Salon</p>
                    <p className="text-charcoal/60 text-sm">{CONTACT.address}</p>
                  </div>
                </div>

                <a
                  href={CONTACT.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 bg-white rounded-2xl p-4 border border-ocean/5 hover:shadow-md transition-all"
                  data-testid="contact-instagram"
                >
                  <div className="w-11 h-11 rounded-xl bg-peachlight flex items-center justify-center flex-shrink-0">
                    <Instagram className="text-sage" size={19} />
                  </div>
                  <div>
                    <p className="font-medium text-sm text-black">Instagram</p>
                    <p className="text-charcoal/60 text-sm">@justjjax</p>
                  </div>
                </a>
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              <form
                onSubmit={handleSubmit}
                className="bg-white rounded-3xl p-8 md:p-10 border border-ocean/5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
                data-testid="work-with-me-form"
              >
                <h3 className="font-display text-xl mb-6">Get in Touch</h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-charcoal mb-1 block">Name *</label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="w-full bg-sand/50 border border-ocean/10 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-sage"
                      data-testid="work-name-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-charcoal mb-1 block">Email *</label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="w-full bg-sand/50 border border-ocean/10 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-sage"
                      data-testid="work-email-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-charcoal mb-1 block">Subject</label>
                    <input
                      type="text"
                      value={form.subject}
                      onChange={(e) => setForm({ ...form, subject: e.target.value })}
                      className="w-full bg-sand/50 border border-ocean/10 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-sage"
                      data-testid="work-subject-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-charcoal mb-1 block">
                      Message *
                    </label>
                    <textarea
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      rows={4}
                      className="w-full bg-sand/50 border border-ocean/10 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-sage resize-none"
                      data-testid="work-message-input"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-olive text-white hover:bg-amber rounded-full px-8 py-3.5 font-medium transition-all duration-300 flex items-center justify-center gap-2"
                    data-testid="work-submit-btn"
                  >
                    <Send size={16} />
                    {loading ? "Sending..." : "Send Message"}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
